import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { IFormData } from "../Forms/formFields";
import { Feature } from "ol";
import { ISummaryParams } from "../Registration/SummarySection";
import { useAppContext } from "./AppContext";
import { IEsriCodedValue } from "../Map/rest";
import { config } from "../Map/config";
import { IFullscreenViewOverlay } from "../Registration/Registration";
import { DrawType, PageId } from "../Map/enums";
import { generateSnapshots } from "../Map/snapshot";
import { getDrawingSymbol } from "../Map/symbols";
import { formatDate } from "../Map/utils";
import { ILVMForestryData } from "../interfaces";

export interface IRegistrationContext {
    userConsent: boolean;
    setUserConsent: Dispatch<SetStateAction<boolean>>;
    validateForm: string;
    setValidateForm: (timeStamp: string) => void;
    resetForm: number;
    setResetForm: Dispatch<SetStateAction<number>>;
    rawData: IFormData;
    setRawData: Dispatch<SetStateAction<IFormData>>;
    formFeature: Feature;
    setFormFeature: Dispatch<SetStateAction<Feature>>;
    attachments: FileList;
    setAttachments: Dispatch<SetStateAction<FileList>>;
    summaryData: ISummaryParams;
    setSummaryData: Dispatch<SetStateAction<ISummaryParams>>;
    getSummaryJson: () => Promise<any>;
    overlay: IFullscreenViewOverlay;
    setOverlay: Dispatch<SetStateAction<IFullscreenViewOverlay>>;
    options: ISelectOptions;
    setOptions: Dispatch<SetStateAction<ISelectOptions>>;
    page: PageId;
    setPage: Dispatch<SetStateAction<PageId>>;
    onFormBack: () => void;
    onFormProceed: () => void;
    onFormUpdated: () => void;
    onValidateForm: (pageId: PageId) => void;
    proceedClicked: boolean;
    setProceedClicked: Dispatch<SetStateAction<boolean>>;
    drawingMenuVisible: boolean;
    setDrawingMenuVisible: Dispatch<SetStateAction<boolean>>; // set `undefined` to toggle
    drawingInfoVisible: boolean;
    setDrawingInfoVisible: Dispatch<SetStateAction<boolean>>;
    searchInfoVisible: boolean;
    setSearchInfoVisible: Dispatch<SetStateAction<boolean>>;
    toggleSearchInfo: () => void;
    conditionAgreed: boolean;
    setConditionAgreed: Dispatch<SetStateAction<boolean>>;
    snapshot: string;
    setSnapshot: Dispatch<SetStateAction<string>>;
    drawnFeatures: Feature[];
    setDrawnFeatures: Dispatch<SetStateAction<Feature[]>>;
    restartApp: () => void;
    LvmForestryData: ILVMForestryData;
    setLvmForestryData: Dispatch<SetStateAction<ILVMForestryData>>;
    LvmForestryCount: number;
    setLvmForestryCount: Dispatch<SetStateAction<number>>;
    applicationId: string;
    setApplicationId: Dispatch<SetStateAction<string>>;
}

interface ISelectOptions {
    [fieldName: string]: IEsriCodedValue[];
}

const RegistrationContext = createContext<IRegistrationContext>(null);

export const useRegistrationContext = (): IRegistrationContext => useContext(RegistrationContext);

export const RegistrationContextProvider: FC = (props: React.PropsWithChildren<IRegistrationContext>) => {
    const [userConsent, setUserConsent] = useState<boolean>(false);
    const [conditionAgreed, setConditionAgreed] = useState<boolean>(false);
    const [validateForm, setValidateForm] = useState<string>("1");
    const [resetForm, setResetForm1] = useState<number>(0);
    const [rawData, setRawData] = useState({});
    const [formFeature, setFormFeature] = useState<Feature>(null);
    const [attachments, setAttachments] = useState<FileList>(null);
    const [summaryData, setSummaryData] = useState<ISummaryParams>();
    const [overlay, setOverlay] = useState<IFullscreenViewOverlay>(null);
    const [options, setOptions] = useState<ISelectOptions>({});
    const [page, setPage] = useState<PageId>(PageId.ApplicantForm);
    // const [page, setPage] = useState<PageId>(PageId.DrawingForm);
    const [proceedClicked, setProceedClicked] = useState<boolean>(false);
    const [drawingMenuVisible, setDrawingMenuVisible] = useState<boolean>(true); // set `undefined` to toggle
    const [drawingInfoVisible, setDrawingInfoVisible] = useState<boolean>(false);
    const [searchInfoVisible, setSearchInfoVisible] = useState<boolean>(false);
    const [snapshot, setSnapshot] = useState<string>();
    const [drawnFeatures, setDrawnFeatures] = useState<Feature[]>([]);
    const [LvmForestryData, setLvmForestryData] = useState<ILVMForestryData>();
    const [LvmForestryCount, setLvmForestryCount] = useState<number>();
    const [applicationId, setApplicationId] = useState<string>();

    const drawingMenuVisibleRef = useRef<boolean>(true);
    const searchInfoVisibleRef = useRef<boolean>(false);

    const { basemap, getFieldValue, getFieldAlias, getDateFormated, fieldInfos, setHasMap } = useAppContext();

    useEffect(() => {
        const target = document.getElementById("timberflowform-map");
        page === PageId.ApplicantForm
            ? target.classList.add("recaptcha-visible")
            : target.classList.remove("recaptcha-visible");
    }, [page]);

    useEffect(() => {
        LvmForestryCount &&
            LvmForestryData &&
            setApplicationId(
                getSummaryId(formFeature.get("OBJECTID"), LvmForestryData.LVMForestryCode, LvmForestryCount)
            );
    }, [LvmForestryCount, formFeature, LvmForestryData]);

    const prepareToResetForm = () => {
        setRawData({});
        setFormFeature(null);
        setValidateForm("1");
        setAttachments(null);
        setResetForm1(Date.now());
        setSummaryData(null);
        setDrawnFeatures([]);
        setSnapshot(null);
        setLvmForestryData(undefined);
        setLvmForestryCount(undefined);
        resetMinMaxDates();
    };
    const restartApp = () => {
        setUserConsent(false);
        setConditionAgreed(false);
        setValidateForm("1");
        setResetForm1(0);
        setRawData({});
        setFormFeature(null);
        setAttachments(null);
        setSummaryData(null);
        setOverlay(null);
        setPage(PageId.ApplicantForm);
        setProceedClicked(false);
        setDrawingMenuVisible(true);
        setDrawingInfoVisible(false);
        setSnapshot(null);
        setDrawnFeatures([]);
        setLvmForestryData(undefined);
        setLvmForestryCount(undefined);
        resetMinMaxDates();
    };

    const getSnapshots = useCallback(async () => {
        drawnFeatures && drawnFeatures.map((f) => f.setStyle(getDrawingSymbol(f.get("DRAWTYPE"), f.get("MASTERNAME"))));
        return await generateSnapshots(drawnFeatures, basemap, setSnapshot);
    }, [drawnFeatures]);

    const getSummaryJson = async (): Promise<ISummaryParams> => {
        const attachedFiles = attachments
            ? {
                  title: "Grafiskie pielikumi:",
                  rows: Array.from(attachments, (attachment) => attachment.name)
              }
            : undefined;

        const summary: ISummaryParams = {
            customer: getCustomerSection(formFeature),
            transporting: getTransportingSection(
                drawnFeatures,
                formFeature,
                LvmForestryData,
                getFieldAlias,
                getFieldValue
            ),
            contactPerson: getContactPersonSection(LvmForestryData),
            applicationId,
            attachments: attachedFiles,
            date: getDateFormated(Date.now())
        };

        drawnFeatures && drawnFeatures.length && (summary.snapshot = await getSnapshots());

        return summary;
    };

    const onFormBack = useCallback(() => {
        if (page === PageId.DrawingForm) {
            document.getElementsByClassName("fs-container")[0].classList.remove("map-form");
            setLvmForestryData(undefined);
            setUserConsent(false);
            setOverlay(null);
            setValidateForm("1");
            setPage(PageId.ApplicantForm);
        } else if (page === PageId.TermForm) {
            setOverlay(null);
            setValidateForm("1");
            setPage(PageId.DrawingForm);
        } else if (page === PageId.ConditionsForm) {
            setOverlay(null);
            setValidateForm("1");
            setPage(PageId.TermForm);
        } else if (page === PageId.SummaryForm) {
            setOverlay(null);
            setValidateForm("1");
            setConditionAgreed(false);
            setPage(PageId.ConditionsForm);
        }
    }, [page]);

    const onFormProceed = useCallback(() => {
        if (page === PageId.Submit) {
            restartApp();
            return;
        }
        setProceedClicked(true);
        onValidateForm(page);
        if (page === PageId.SummaryForm || page === PageId.ConditionsForm) {
            // TODO deselect drawn features, when proceed in drawing form
            onFormUpdated();
        }
    }, [page]);

    const onFormUpdated = useCallback(() => {
        if (page === PageId.ApplicantForm) {
            setPage(PageId.DrawingForm);
            setHasMap(true);
        } else if (page === PageId.DrawingForm) {
            document.getElementsByClassName("fs-container")[0].classList.remove("map-form");
            setPage(PageId.TermForm);
        } else if (page === PageId.TermForm) {
            setPage(PageId.ConditionsForm);
        } else if (page === PageId.ConditionsForm) {
            setPage(PageId.SummaryForm);
        } else if (page === PageId.SummaryForm) {
            setPage(PageId.Submit);
        }
    }, [page]);

    const onValidateForm = (id: PageId) => {
        setValidateForm(id + "-" + Date.now());
    };

    const setDrawingMenuVisibleReference = (visible) => {
        drawingMenuVisibleRef.current = visible === undefined ? !drawingMenuVisibleRef.current : visible;
        setDrawingMenuVisible(drawingMenuVisibleRef.current);
    };

    const toggleSearchInfo = () => {
        searchInfoVisibleRef.current = !searchInfoVisibleRef.current;
        setSearchInfoVisible(searchInfoVisibleRef.current);
    };

    const values = {
        userConsent,
        setUserConsent,
        validateForm,
        setValidateForm,
        resetForm,
        setResetForm: prepareToResetForm,
        rawData,
        setRawData,
        formFeature,
        setFormFeature,
        attachments,
        setAttachments,
        summaryData,
        setSummaryData,
        getSummaryJson,
        overlay,
        setOverlay,
        options,
        setOptions,
        page,
        setPage,
        onFormBack,
        onFormProceed,
        onFormUpdated,
        onValidateForm,
        proceedClicked,
        setProceedClicked,
        drawingMenuVisible,
        setDrawingMenuVisible: setDrawingMenuVisibleReference,
        searchInfoVisible,
        setSearchInfoVisible,
        toggleSearchInfo,
        drawingInfoVisible,
        setDrawingInfoVisible,
        conditionAgreed,
        setConditionAgreed,
        snapshot,
        setSnapshot,
        drawnFeatures,
        setDrawnFeatures,
        restartApp,
        LvmForestryData,
        setLvmForestryData,
        LvmForestryCount,
        setLvmForestryCount,
        applicationId,
        setApplicationId
    };

    return <RegistrationContext.Provider value={values}>{props.children}</RegistrationContext.Provider>;
};

const getCustomerSection = (formFeature: Feature) => {
    const rows = [
        formFeature.get("APPLICANTNAME"),
        formFeature.get("APPLICANTADDRESS"),
        formFeature.get("APPLICANTPHONE"),
        formFeature.get("APPLICANTEMAIL"),
        "Kontaktpersona: " + formFeature.get("CONTACTPERSON") + ", tel." + formFeature.get("CONTACTPERSONPHONE")
    ];
    const isCompany = formFeature.get("APPLICANTTYPE").toString() === "1";
    isCompany && rows.splice(1, 0, formFeature.get("APPLICANTCODE"));

    return { title: "Dati par iesniedzēju", rows };
};

const getTransportingSection = (
    drawnFeatures: Feature[],
    formFeature: Feature,
    LvmForestryData: ILVMForestryData,
    getFieldAlias: CallableFunction,
    getFieldValue: CallableFunction
) => {
    const propertyFeature = drawnFeatures.find((f) => f.get("DRAWTYPE") === DrawType.PolygonProperty);
    const rows = [
        {
            alias: getFieldAlias("VOLUME") + ": ",
            value: formFeature.get("VOLUME") + "m\u00B3"
        },
        {
            alias: "Darbības termiņš: ",
            value: getFieldValue(formFeature, "DATEFROM") + "-" + getFieldValue(formFeature, "DATETO")
        },
        { alias: "Īpašuma nosaukums: ", value: propertyFeature.get("MASTERNAME") },
        { alias: getFieldAlias("CADASTER") + ": ", value: propertyFeature.get("CADASTER") }
    ];
    const points =
        drawnFeatures.length &&
        drawnFeatures.filter((f) => {
            const drawtype = f.get("DRAWTYPE");
            return drawtype === DrawType.PointYard;
        });
    const lines = drawnFeatures.length && drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.PolylineRoad);
    const LVMlines =
        drawnFeatures.length && drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.PolylineLvmRoad);

    if (points.length) {
        const pointTitles = points
            .map((point) => point.get("DESCRIPTION"))
            .filter((p) => p)
            .join("; ");
        rows.push({
            alias: "Krautuve" + (points.length > 1 ? "s" : "") + ": ",
            value: points.length + (pointTitles ? ", " : "") + pointTitles
        });
    }
    if (lines.length) {
        const linesTitles = lines
            .map((line) => line.get("DESCRIPTION"))
            .filter((l) => l)
            .join("; ");
        rows.push({
            alias: "Pievešanas ceļ" + (lines.length === 1 ? "š" : "i") + ": ",
            value: lines.length + (linesTitles ? ", " : "") + linesTitles
        });
    }
    if (LVMlines.length) {
        const linesTitles = LVMlines.map((line) => {
            return (
                (line.get("MASTERNAME") || "bez nosaukuma") +
                (line.get("DESCRIPTION") ? " (" + line.get("DESCRIPTION") + ")" : "")
            );
        })
            .filter((l) => l)
            .join("; ");
        rows.push({
            alias: "LVM ceļ" + (LVMlines.length === 1 ? "š" : "i") + ": ",
            value: LVMlines.length + (linesTitles ? ", " : "") + linesTitles
        });
    }
    rows.push(
        { alias: "LVM iecirknis: ", value: (LvmForestryData && LvmForestryData.LVMDistrictName) || "" },
        { alias: "LVM reģions: ", value: (LvmForestryData && LvmForestryData.LVMForestryName) || "" }
    );

    const note = formFeature.get("DESCRIPTION");
    note && rows.push({ alias: getFieldAlias("DESCRIPTION") + ": ", value: note });

    return { title: "Ar LVM saskaņojamā darbība", rows };
};

const getContactPersonSection = (LvmForestryData: ILVMForestryData) => {
    const rows = [
        LvmForestryData.LVMManagerName || "kontaktpersonas vārds",
        LvmForestryData.LVMManagerPhone || "telefona nr.",
        LvmForestryData.LVMManagerMail || "epasts"
    ];

    return { title: "LVM kontaktpersona", rows };
};

const getSummaryId = (oid: string, LVMFORESTRYCODE: number, LvmForestryCount?: number) => {
    const date = new Date(Date.now());
    const year = date.getUTCFullYear();
    const id =
        year +
        "_" +
        LVMForestryByCode[LVMFORESTRYCODE] +
        (LvmForestryCount && LvmForestryCount > 1 ? "-V" : "") +
        "_KK_" +
        oid;
    return id;
};

const LVMForestryByCode = {
    1: "AV", // "Austrumvidzemes reģions"
    2: "DK", // "Dienvidkurzemes reģions"
    3: "DL", // "Dienvidlatgales reģions"
    4: "RV", // "Rietumvidzemes reģions"
    5: "VD", // "Vidusdaugavas reģions"
    6: "ZG", // "Zemgales reģions"
    7: "ZK", // "Ziemeļkurzemes reģions"
    8: "ZL" // "Ziemeļlatgales reģions"
};

const resetMinMaxDates = () =>
    config.forms.termForm.fields.map((f) => {
        f.minDate && (f.minDate = undefined);
        f.maxDate && (f.maxDate = undefined);
    });
